import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  FileText,
  Cloud,
  Upload,
  Download,
  RefreshCw,
  Search,
  Grid,
  List,
  MoreVertical,
  Star,
  StarOff,
  Trash2,
  ExternalLink,
  Copy,
  Calendar,
  ClipboardList,
  Users,
  MessageSquare,
  FileSpreadsheet,
  Presentation,
  CheckSquare,
  LayoutTemplate,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/integrations/supabase/client';
import { triggerIntegrationSync } from '@/lib/integrationSync';
import { formatDistanceToNow } from 'date-fns';
import DocumentViewerModal, { type ProjectDocument } from './DocumentViewerModal';

const DOCS_BUCKET = 'project-documents';

// Bounds how many extracted-but-unanalyzed documents get an analyze-documents
// invoke fired per "Sync All" click — mirrors microsoft-graph-connector's
// MAX_FILES_PER_SYNC / run-scheduled-syncs' MAX_DOCUMENTS_TO_ANALYZE_PER_SYNC
// convention (both 25) so a single manual sync can't fire an unbounded burst
// of LLM calls. Anything beyond this cap stays extraction_status = 'extracted'
// and is picked up by the next scheduled cron tick or a subsequent Sync All.
const MAX_DOCUMENTS_TO_ANALYZE_PER_CLICK = 25;

const mimeToType = (mime: string | null): 'doc' | 'spreadsheet' | 'presentation' | 'pdf' | 'folder' => {
  if (!mime) return 'doc';
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return 'spreadsheet';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'presentation';
  return 'doc';
};

const formatSize = (bytes: number | null): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface Template {
  id: string;
  name: string;
  description: string;
  category: 'planning' | 'meeting' | 'communication' | 'tracking' | 'reporting';
  type: 'doc' | 'spreadsheet' | 'presentation';
  icon: React.ReactNode;
  usageCount: number;
  lastUsed: string;
}


const mockTemplates: Template[] = [
  {
    id: 't1',
    name: 'Project Kickoff Meeting',
    description: 'Comprehensive agenda and notes template for project kickoff meetings with stakeholders',
    category: 'meeting',
    type: 'doc',
    icon: <Calendar className="w-6 h-6" />,
    usageCount: 24,
    lastUsed: '2 days ago',
  },
  {
    id: 't2',
    name: 'Project Schedule Plan',
    description: 'Gantt chart and milestone tracking template for project timeline management',
    category: 'planning',
    type: 'spreadsheet',
    icon: <FileSpreadsheet className="w-6 h-6" />,
    usageCount: 18,
    lastUsed: '1 week ago',
  },
  {
    id: 't3',
    name: 'Minutes of Meeting (MOM)',
    description: 'Structured template for recording meeting decisions, action items, and attendees',
    category: 'meeting',
    type: 'doc',
    icon: <ClipboardList className="w-6 h-6" />,
    usageCount: 56,
    lastUsed: 'Today',
  },
  {
    id: 't4',
    name: 'Communication Matrix',
    description: 'Stakeholder communication plan with frequency, channels, and responsibilities',
    category: 'communication',
    type: 'spreadsheet',
    icon: <MessageSquare className="w-6 h-6" />,
    usageCount: 12,
    lastUsed: '3 days ago',
  },
  {
    id: 't5',
    name: 'RACI Matrix',
    description: 'Responsibility assignment matrix for clear role definitions across tasks',
    category: 'planning',
    type: 'spreadsheet',
    icon: <Users className="w-6 h-6" />,
    usageCount: 31,
    lastUsed: '5 days ago',
  },
  {
    id: 't6',
    name: 'Status Report',
    description: 'Weekly/monthly project status report with KPIs, risks, and progress updates',
    category: 'reporting',
    type: 'presentation',
    icon: <Presentation className="w-6 h-6" />,
    usageCount: 42,
    lastUsed: 'Yesterday',
  },
  {
    id: 't7',
    name: 'Risk Register',
    description: 'Template for identifying, assessing, and tracking project risks and mitigations',
    category: 'tracking',
    type: 'spreadsheet',
    icon: <CheckSquare className="w-6 h-6" />,
    usageCount: 15,
    lastUsed: '1 week ago',
  },
  {
    id: 't8',
    name: 'Project Charter',
    description: 'Formal document outlining project objectives, scope, stakeholders, and success criteria',
    category: 'planning',
    type: 'doc',
    icon: <FileText className="w-6 h-6" />,
    usageCount: 9,
    lastUsed: '2 weeks ago',
  },
];

interface OneDriveIntegration {
  id: string;
  is_active: boolean;
  last_sync_at: string | null;
}

interface DocumentsViewProps {
  // Navigates to the Document Intelligence review view (an activeView
  // within Index.tsx's renderContent switch, not a router route) — mirrors
  // the onNavigateToActionHub/onNavigateToGovernance callback-prop
  // convention already used by MeetingHub/ActionHub.
  onNavigateToDocumentIntelligence?: () => void;
}

const DocumentsView = ({ onNavigateToDocumentIntelligence }: DocumentsViewProps) => {
  const { user, tenantId } = useAuth();
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | 'all'>('all');
  const [syncingAll, setSyncingAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<ProjectDocument | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'documents' | 'templates'>('documents');
  const [oneDrive, setOneDrive] = useState<OneDriveIntegration | null>(null);
  const [oneDriveLoading, setOneDriveLoading] = useState(true);
  const [suggestedCandidateCount, setSuggestedCandidateCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    const { data, error } = await supabase
      .from('project_documents')
      .select('id, name, file_path, mime_type, size_bytes, starred, created_at, project_id, summary, summary_generated_at, projects_sync(name)')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setDocuments(
        data.map((d) => ({
          id: d.id,
          name: d.name,
          file_path: d.file_path,
          mime_type: d.mime_type,
          size_bytes: d.size_bytes,
          starred: d.starred,
          created_at: d.created_at,
          project_id: d.project_id,
          project_name: (d.projects_sync as { name: string } | null)?.name ?? null,
          summary: d.summary,
          summary_generated_at: d.summary_generated_at,
        }))
      );
    }
    setDocumentsLoading(false);
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const loadOneDriveIntegration = useCallback(async () => {
    setOneDriveLoading(true);
    const { data, error } = await supabase
      .from('integration_configs')
      .select('id, is_active, last_sync_at')
      .eq('integration_type', 'onedrive_documents')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error) {
      setOneDrive(data as OneDriveIntegration | null);
    }
    setOneDriveLoading(false);
  }, []);

  const loadSuggestedCandidateCount = useCallback(async () => {
    const { count, error } = await supabase
      .from('document_candidates')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'suggested');
    if (!error) {
      setSuggestedCandidateCount(count ?? 0);
    }
  }, []);

  useEffect(() => { loadOneDriveIntegration(); }, [loadOneDriveIntegration]);
  useEffect(() => { loadSuggestedCandidateCount(); }, [loadSuggestedCandidateCount]);

  const projects = [...new Set(documents.map((d) => d.project_name).filter((p): p is string => !!p))];

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = selectedProject === 'all' || doc.project_name === selectedProject;
    return matchesSearch && matchesProject;
  });

  const getTypeIcon = (mime: string | null) => {
    switch (mimeToType(mime)) {
      case 'spreadsheet':
        return <FileText className="w-5 h-5 text-success" />;
      case 'presentation':
        return <FileText className="w-5 h-5 text-warning" />;
      case 'pdf':
        return <FileText className="w-5 h-5 text-destructive" />;
      default:
        return <FileText className="w-5 h-5 text-primary" />;
    }
  };

  const handleSyncAll = async () => {
    if (!oneDrive || !oneDrive.is_active) {
      toast({
        title: 'OneDrive not connected',
        description: 'Connect OneDrive from Integrations before syncing documents.',
        variant: 'destructive',
      });
      return;
    }

    setSyncingAll(true);
    toast({ title: 'Syncing', description: 'Syncing files from OneDrive...' });
    try {
      await triggerIntegrationSync(oneDrive.id, 'onedrive_documents');
      await Promise.all([loadDocuments(), loadOneDriveIntegration()]);

      // Extraction (above) only pulls file text into synced_documents —
      // it does not itself produce document_candidates. analyze-documents
      // (Task 5) is the step that turns extracted text into candidates, and
      // otherwise only runs on the next scheduled cron tick, which can be
      // an hour or more away. Trigger it here for anything this sync left
      // extracted-but-unanalyzed, bounded to
      // MAX_DOCUMENTS_TO_ANALYZE_PER_CLICK — same shape as
      // run-scheduled-syncs' own post-sync analyze loop.
      const { data: pendingDocs, error: pendingErr } = await supabase
        .from('synced_documents')
        .select('id')
        .eq('extraction_status', 'extracted')
        .limit(MAX_DOCUMENTS_TO_ANALYZE_PER_CLICK);

      if (pendingErr) {
        // Extraction still succeeded — only the analysis phase couldn't be
        // kicked off. Report sync as complete, but surface that analysis
        // didn't run rather than implying full completion.
        toast({
          title: 'Sync Complete',
          description: 'Files synced, but could not check for documents to analyze.',
        });
      } else if ((pendingDocs ?? []).length === 0) {
        toast({ title: 'Sync Complete', description: 'All documents are up to date.' });
      } else {
        toast({ title: 'Analyzing', description: `Analyzing ${pendingDocs!.length} document(s)...` });

        let analyzed = 0;
        let failed = 0;
        for (const row of pendingDocs!) {
          try {
            const { error: analyzeError } = await supabase.functions.invoke('analyze-documents', {
              body: { syncedDocumentId: row.id },
            });
            if (analyzeError) throw analyzeError;
            analyzed++;
          } catch (analyzeErr) {
            failed++;
            console.error(`analyze-documents failed for synced_documents.id=${row.id}:`, analyzeErr);
          }
        }

        toast({
          title: 'Sync Complete',
          description: failed === 0
            ? `${analyzed} document(s) analyzed.`
            : `${analyzed} document(s) analyzed, ${failed} failed — they'll be retried on the next sync.`,
          variant: failed > 0 && analyzed === 0 ? 'destructive' : undefined,
        });
      }

      // Candidates from the analyze calls above are already written by the
      // time this runs (each invoke was awaited in sequence), so no delay
      // is needed here — unlike a fire-and-forget trigger would require.
      await loadSuggestedCandidateCount();
    } catch (err) {
      toast({
        title: 'Sync failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleConnectOneDrive = () => {
    navigate(tenantSlug ? `/${tenantSlug}/integrations` : '/integrations');
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file || !user) return;

    setUploading(true);
    try {
      const path = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from(DOCS_BUCKET).upload(path, file);
      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase.from('project_documents').insert({
        user_id: user.id,
        tenant_id: tenantId,
        name: file.name,
        file_path: path,
        mime_type: file.type || null,
        size_bytes: file.size,
      });
      if (insertError) throw insertError;

      toast({ title: 'Document uploaded', description: file.name });
      loadDocuments();
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDocument = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage.from(DOCS_BUCKET).createSignedUrl(doc.file_path, 60);
    if (error || !data) {
      toast({ title: 'Could not open document', description: error?.message, variant: 'destructive' });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage.from(DOCS_BUCKET).createSignedUrl(doc.file_path, 60, { download: doc.name });
    if (error || !data) {
      toast({ title: 'Could not download document', description: error?.message, variant: 'destructive' });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteDocument = async (doc: ProjectDocument) => {
    const { error: storageError } = await supabase.storage.from(DOCS_BUCKET).remove([doc.file_path]);
    if (storageError) {
      toast({ title: 'Delete failed', description: storageError.message, variant: 'destructive' });
      return;
    }
    const { error: dbError } = await supabase.from('project_documents').delete().eq('id', doc.id);
    if (dbError) {
      toast({ title: 'Delete failed', description: dbError.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Document deleted' });
    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
  };

  const toggleStar = async (doc: ProjectDocument) => {
    setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, starred: !d.starred } : d)));
    const { error } = await supabase.from('project_documents').update({ starred: !doc.starred }).eq('id', doc.id);
    if (error) {
      // revert on failure
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, starred: doc.starred } : d)));
      toast({ title: 'Could not update star', description: error.message, variant: 'destructive' });
    }
  };

  const handleSummaryGenerated = (docId: string, summary: string, summaryGeneratedAt: string) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === docId ? { ...d, summary, summary_generated_at: summaryGeneratedAt } : d))
    );
    setViewerDoc((prev) => (prev && prev.id === docId ? { ...prev, summary, summary_generated_at: summaryGeneratedAt } : prev));
  };

  const categories = [...new Set(mockTemplates.map((t) => t.category))];

  const filteredTemplates = mockTemplates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category: Template['category']) => {
    switch (category) {
      case 'planning':
        return 'bg-primary/10 text-primary border-primary/30';
      case 'meeting':
        return 'bg-success/10 text-success border-success/30';
      case 'communication':
        return 'bg-warning/10 text-warning border-warning/30';
      case 'tracking':
        return 'bg-destructive/10 text-destructive border-destructive/30';
      case 'reporting':
        return 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleUseTemplate = (template: Template) => {
    toast({
      title: 'Template Created',
      description: `New document created from "${template.name}" template`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="masira-fade-in">
        <h2 className="text-2xl font-bold text-foreground">Documents</h2>
        <p className="text-muted-foreground">Project files synced with OneDrive</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'documents' | 'templates')} className="w-full">
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="documents" className="data-[state=active]:bg-background">
            <FileText className="w-4 h-4 me-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-background">
            <LayoutTemplate className="w-4 h-4 me-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-6 mt-6">
          {/* Sync Status Bar */}
          <div className="masira-card p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0078D4]/10 flex items-center justify-center">
                    <Cloud className="w-4 h-4 text-[#0078D4]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">OneDrive</p>
                    {oneDriveLoading ? (
                      <p className="text-xs text-muted-foreground">Checking...</p>
                    ) : oneDrive?.is_active ? (
                      <p className="text-xs text-success">
                        Connected
                        {oneDrive.last_sync_at && (
                          <span className="text-muted-foreground">
                            {' '}· Last synced {formatDistanceToNow(new Date(oneDrive.last_sync_at), { addSuffix: true })}
                          </span>
                        )}
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">Not connected</p>
                        <button
                          onClick={handleConnectOneDrive}
                          className="text-xs text-primary hover:underline"
                        >
                          Connect OneDrive
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSyncAll} disabled={syncingAll || oneDriveLoading}>
                  <RefreshCw className={cn('w-4 h-4 me-2', syncingAll && 'animate-spin')} />
                  {syncingAll ? 'Syncing...' : 'Sync All'}
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />
                <Button size="sm" onClick={handleUploadClick} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Upload className="w-4 h-4 me-2" />}
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>
            {suggestedCandidateCount > 0 && (
              <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                <p className="text-xs text-foreground">
                  {suggestedCandidateCount} detected project{suggestedCandidateCount === 1 ? '' : 's'} awaiting review
                </p>
                <button
                  onClick={() => onNavigateToDocumentIntelligence?.()}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Review detected projects
                </button>
              </div>
            )}
          </div>

          {/* Filters & Search */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-10"
                />
              </div>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="all">All Projects</option>
                {projects.map((project) => (
                  <option key={project} value={project}>
                    {project}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-secondary/50">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'grid' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
                )}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'list' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
                )}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Documents Grid/List */}
          {documentsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">No documents yet. Upload one to get started.</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDocuments.map((doc, index) => (
                <div
                  key={doc.id}
                  onClick={() => { setViewerDoc(doc); setViewerOpen(true); }}
                  className="masira-card p-4 hover:border-primary/30 transition-all cursor-pointer masira-slide-up group"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(doc.mime_type)}
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleStar(doc)} className="text-muted-foreground hover:text-warning">
                        {doc.starred ? (
                          <Star className="w-4 h-4 fill-warning text-warning" />
                        ) : (
                          <StarOff className="w-4 h-4" />
                        )}
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover">
                          <DropdownMenuItem onClick={() => handleOpenDocument(doc)}>
                            <ExternalLink className="w-4 h-4 me-2" />
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                            <Download className="w-4 h-4 me-2" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteDocument(doc)}>
                            <Trash2 className="w-4 h-4 me-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <h4 className="font-medium text-foreground truncate mb-1">{doc.name}</h4>
                  <p className="text-xs text-muted-foreground mb-3">{doc.project_name ?? 'Unfiled'}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{formatSize(doc.size_bytes)}</span>
                    <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="nexus-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">Project</th>
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">Modified</th>
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">Size</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      onClick={() => { setViewerDoc(doc); setViewerOpen(true); }}
                      className="border-b border-border hover:bg-secondary/30 transition-colors group cursor-pointer"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <button onClick={(e) => { e.stopPropagation(); toggleStar(doc); }} className="text-muted-foreground hover:text-warning">
                            {doc.starred ? (
                              <Star className="w-4 h-4 fill-warning text-warning" />
                            ) : (
                              <StarOff className="w-4 h-4" />
                            )}
                          </button>
                          {getTypeIcon(doc.mime_type)}
                          <span className="font-medium text-foreground">{doc.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">{doc.project_name ?? 'Unfiled'}</td>
                      <td className="p-4 text-sm text-muted-foreground">{formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}</td>
                      <td className="p-4 text-sm text-muted-foreground">{formatSize(doc.size_bytes)}</td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover">
                            <DropdownMenuItem onClick={() => handleOpenDocument(doc)}>
                              <ExternalLink className="w-4 h-4 me-2" />
                              Open
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                              <Download className="w-4 h-4 me-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteDocument(doc)}>
                              <Trash2 className="w-4 h-4 me-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="space-y-6 mt-6">
          {/* Templates Header */}
          <div className="nexus-card p-4 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Project Templates</h3>
              <p className="text-sm text-muted-foreground">Standardized templates for project documentation</p>
            </div>
            <Button size="sm">
              <Upload className="w-4 h-4 me-2" />
              Upload Template
            </Button>
          </div>

          {/* Template Filters */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ps-10"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-10 px-3 rounded-md border border-input bg-background text-sm capitalize"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category} className="capitalize">
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTemplates.map((template, index) => (
              <div
                key={template.id}
                className="nexus-card p-5 hover:border-primary/30 transition-all cursor-pointer nexus-slide-up group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    {template.icon}
                  </div>
                  <Badge variant="outline" className={cn('capitalize text-xs', getCategoryColor(template.category))}>
                    {template.category}
                  </Badge>
                </div>
                <h4 className="font-semibold text-foreground mb-2">{template.name}</h4>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{template.description}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                  <span>Used {template.usageCount} times</span>
                  <span>Last: {template.lastUsed}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => handleUseTemplate(template)}
                  >
                    <Copy className="w-4 h-4 me-2" />
                    Use Template
                  </Button>
                  <Button size="sm" variant="outline">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <DocumentViewerModal
        document={viewerDoc}
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        onOpenOriginal={handleOpenDocument}
        onDownload={handleDownloadDocument}
        onDelete={(doc) => { handleDeleteDocument(doc); setViewerOpen(false); }}
        onBrowseTemplates={() => { setViewerOpen(false); setActiveTab('templates'); }}
        onSummaryGenerated={handleSummaryGenerated}
      />
    </div>
  );
};

export default DocumentsView;
